import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval, createStorePerformanceRequest } from '../../../../../constants';
import {
  SalesInsightsResponse,
  SalesItem,
  useAsyncAction,
  useStoreOpeningHours,
  useTransaction,
} from '../../../../../hooks';
import { Icons } from '../../../../../stories/atoms';
import { ContainerV2 } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { homeTranslations } from '../../../../../translations';
import { getClippedDailyPerformanceItems, isFailureResponse } from '../../../../../utils';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import { SalesInsightTable } from './salesInsightTable/SalesInsightTable';

export type SalesInsightsWidgetProps = {
  currentDate: Date;
  interval: StorePerformanceInterval;
};

export const SalesInsightsWidget: FC<SalesInsightsWidgetProps> = observer(({ currentDate, interval }) => {
  const { t } = useTranslation();
  const { fetchSalesInsightsData } = useTransaction();
  const { getStoreOpeningHours } = useStoreOpeningHours();
  const { storeOpeningHour, storeClosingHour } = getStoreOpeningHours(currentDate);
  const { salesInsightWidget: salesInsightWidgetTranslations } = homeTranslations;
  const [clippedItems, setClippedItems] = useState<SalesItem[]>([]);
  const [saleWithoutReturns, setSaleWithoutReturns] = useState(0);

  const {
    result: salesInsightData,
    isLoading: salesInsightLoading,
    isError: salesInsightError,
    triggerAction: triggerSalesInsightAction,
  } = useAsyncAction<SalesInsightsResponse>();

  const fetchData = useCallback(async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });

    const response = await fetchSalesInsightsData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }
    setSaleWithoutReturns(response.data.totalReceiptsWithoutReturns);

    return response.data;
  }, [interval, currentDate]);

  useEffect(() => {
    triggerSalesInsightAction(fetchData);
  }, [currentDate]);

  useEffect(() => {
    const updatedClippedItems = getClippedDailyPerformanceItems({
      interval,
      storeOpeningHour,
      storeClosingHour,
      storePerformanceItems: salesInsightData?.salesInsight ?? [],
      getHasValue: (item) => item.sales !== 0,
    });

    setClippedItems(updatedClippedItems);
  }, [currentDate, salesInsightData]);

  const isAllZero = (data: SalesItem[]) =>
    data.every((item) =>
      Object.entries(item)
        .filter(([key]) => key !== 'timeUnit')
        .every(([, value]) => value === 0),
    );

  const isNoData = salesInsightData
    ? isAllZero(clippedItems) || salesInsightData.salesInsight.length === 0
    : false;

  return (
    <WidgetCardContent
      headerIcon={Icons.PROFILE}
      headerText={t(salesInsightWidgetTranslations.title.key, salesInsightWidgetTranslations.title.defaultValue)}
      subHeaderText={t(
        salesInsightWidgetTranslations.subTitle.key,
        salesInsightWidgetTranslations.subTitle.defaultValue,
      )}
      lastUpdatedDate={salesInsightData?.lastUpdated}
      isLoading={salesInsightLoading}
      isError={salesInsightError}
      errorText={t(salesInsightWidgetTranslations.error.key, salesInsightWidgetTranslations.error.defaultValue)}
      toolTipText={t(
        salesInsightWidgetTranslations.tooltip.key,
        salesInsightWidgetTranslations.tooltip.defaultValue,
      )}
      isNoData={isNoData}
      content={
        <ContainerV2 data-testid="sales-insights-widget-wrapper" height="100%" overflow="auto">
          <SalesInsightTable
            tableData={clippedItems}
            saleWithoutReturns={saleWithoutReturns}
            currentDate={currentDate}
            interval={interval}
          />
        </ContainerV2>
      }
    />
  );
});
